/**
 * @fileoverview Workflow role picker
 *
 * Picks one workflow role (or none) and shows it as the same soft badge the
 * rest of the app uses, so a role reads identically in the org settings team
 * table and in the create-job team table. Search is included because a firm
 * can define many roles.
 */

import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/ui/composed/menus";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/ui/composed/overlays";
import { cn } from "@/utils/misc";
import { RoleBadge, type RoleBadgeRole } from "./RoleBadge";

export type WorkflowRoleOption = { _id: Id<"workflowRoles"> } & RoleBadgeRole;

export function WorkflowRoleSelect({
  roles,
  value,
  onChange,
  disabled = false,
}: {
  roles: WorkflowRoleOption[];
  value: Id<"workflowRoles"> | null;
  onChange: (roleId: Id<"workflowRoles"> | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const current = roles.find((r) => r._id === value);
  // A role archived or deleted after being selected: flag it rather than
  // rendering an empty-looking "None".
  const missing = value !== null && !current;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="inline-flex h-8 w-full items-center gap-2 rounded-md border border-input bg-background px-2 text-xs transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {current ? (
            <RoleBadge role={current} size="sm" />
          ) : (
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-left",
                missing ? "italic text-muted-foreground" : "text-primary/60",
              )}
            >
              {missing ? "Removed role" : "None"}
            </span>
          )}
          <ChevronDown className="ml-auto size-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search roles..." className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty className="py-4 text-center text-xs text-primary/60">
              No matching roles.
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="None"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="gap-2 text-xs"
              >
                <Check
                  className={cn(
                    "size-3.5",
                    value === null ? "opacity-100" : "opacity-0",
                  )}
                />
                None
              </CommandItem>
            </CommandGroup>
            {roles.length > 0 && (
              <CommandGroup heading="Roles">
                {roles.map((role) => (
                  <CommandItem
                    key={role._id}
                    value={role.name}
                    onSelect={() => {
                      onChange(role._id);
                      setOpen(false);
                    }}
                    className="gap-2 text-xs"
                  >
                    <Check
                      className={cn(
                        "size-3.5",
                        value === role._id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <RoleBadge role={role} size="sm" />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
